<template>
    <div class="main report">

        <div class="report-heading">
            <h1><span class="breadcrumb" @click="clearFilters()">Warehouse Orders</span><i v-if="distributor_name" class="fas fa-chevron-right"></i>{{ distributor_name }}</h1>
            <div v-if="notEmpty(totals)" class="report-total">
                <div>
                    <label>Total Warehouse Cost</label><br>{{ formatCurrency(totals.cost) }}
                </div>
                <div>
                    <label>Total Warehouse Retail</label><br>{{ formatCurrency(totals.price) }}
                </div>
                <div>
                    <label>Total Margin</label><br>{{ totals.margin }}
                </div>
            </div>
        </div>

        <div class="filters with-compare">
            <div class="three-columns selectors">
                <label>UPC / SKU</label>
                <label v-if="showDealerFilter">Dealer</label>
                <label v-else></label>
                <label></label>
                <div class="input-with-control">
                    <input class="warehouse" type="text" v-model="filters.search_text" placeholder="Search..." @keyup.enter="setFilter('search_text')">
                    <div v-if="filters.search_text" class="button input-clear" @click="setFilter('search_text', 'clear')"><i class="fas fa-times"></i></div>
                    <div class="button warehouse input-go" @click="setFilter('search_text')"><i class="fas fa-search"></i></div>
                </div>
                <div v-if="showDealerFilter" class="input-with-control">
                    <input class="warehouse" type="text" v-model="filters.location_name" placeholder="Search..." @keyup.enter="setFilter('location')">
                    <div class="button warehouse input-go" @click="setFilter('location')"><i class="fas fa-search"></i></div>
                    <div v-if="dealerSearchList.length > 0 || dealerSearchMessage" class="search-list">
                        <div v-for="dealer in dealerSearchList" class="search-list-item" :key="dealer.location_id" @click="setFilterFromSearch(location)">{{ dealer.description }}</div>
                        <div v-if="dealerSearchMessage">{{ dealerSearchMessage }}</div>
                    </div>
                </div>
                <div v-else></div>
            </div>
            <div>
                <div class="input-with-control date-control">
                    <label>Start</label>
                    <label>End</label>
                    <label></label>
                    <input class="warehouse" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.start_date" required="required">
                    <input class="warehouse" type="date" :min="minFilterDate" :max="maxFilterDate" v-model="filters.end_date" required="required">
                    <div class="button warehouse input-go" @click="setFilterDates()"><i class="fas fa-calendar-check"></i></div>
                </div>
            </div>

            <div>
                <div class="button warehouse compare-button" @click="showCompare = true"><i class="fas fa-calendar-plus"></i></div>
            </div>

        </div>

        <div v-if="message" class="empty-list">
            {{ message }}
            <div v-if="loading" class="loading"><i class="fas fa-spinner fa-spin"></i></div>
        </div>

        <div v-if="distributors.length > 0" class="report-list warehouse">
            <div class="report-row heading">
                <h2>Distributor</h2>
                <label></label>
                <label class="right">Items Ordered</label>
                <label class="right">Total Cost of<br>Goods Ordered</label>
                <label class="right">Total Retail<br>Price Ordered</label>
                <label class="right">Profit</label>
                <label class="right">Margin</label>
                <label class="right">% of Total<br>Whs Retail</label>
                <label class="right">Average<br>Item Price</label>
            </div>
            <div class="report-row" v-for="(data, index) in distributors" :key="index">
                <div>
                    <span class="row-title">{{ data.description }}</span>
                    <div class="row-controls">
                        <div><div class="button warehouse" @click="getListItems(data)"><i class="fas fa-list"></i></div></div>
                    </div>
                </div>
                <div>
                    <label class="full bordered">POS</label><br>
                    <label class="full bordered">Web</label><br>
                    <label class="full bordered">Assembly</label><br>
                    <label class="full">Total</label>
                </div>
                <div>
                    <span class="numbers full bordered">{{ data.store_quantity || "0" }}</span><br>
                    <span class="numbers full bordered">{{ data.web_quantity || "0" }}</span><br>
                    <span class="numbers full bordered">{{ data.assembly_quantity || "0" }}</span><br>
                    <span class="numbers full">{{ data.quantity }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_cost) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_cost) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_cost) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.cost) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_price) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.price) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_profit) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_profit) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_profit) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.profit) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ data.store_margin || "0.0" }}</span><br>
                    <span class="numbers full bordered">{{ data.web_margin || "0.0" }}</span><br>
                    <span class="numbers full bordered">{{ data.assembly_margin || "0.0" }}</span><br>
                    <span class="numbers full">{{ data.margin || "0.0" }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full">{{ percentOfTotal(data) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.pos_average_item_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_average_item_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_average_item_price) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.average_item_price) }}</span>
                </div>
            </div>
        </div>

        <div v-if="items.length > 0" class="report-list">
            <div class="report-row supplier-items heading">
                <h2>Item</h2>
                <label></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'items_ordered' }]" @click="setSort('items_ordered')">Items Ordered<span v-html="showSort('items_ordered')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'cost_ordered' }]" @click="setSort('cost_ordered')">Total Cost of<br>Goods Ordered<span v-html="showSort('cost_ordered')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'price_ordered' }]" @click="setSort('price_ordered')">Total Retail<br>Price Ordered<span v-html="showSort('price_ordered')"></span></label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'total_profit' }]" @click="setSort('total_profit')">Profit<span v-html="showSort('total_profit')"></span></label>
                <label class="right">Margin</label>
                <label class="right">% of Total<br>Whs Retail</label>
                <label :class="['right sortable', { sortcurrent: filters.sort_column == 'average_item_price' }]" @click="setSort('average_item_price')">Average<br>Item Price<span v-html="showSort('average_item_price')"></span></label>
            </div>
            <div class="report-row supplier-items" v-for="(data, index) in items" :key="index">
                <div>
                    <span class="row-title">{{ data.description }}</span>
                    <div class="two-columns row-controls">
                        <div><label>UPC</label>{{ data.upc }}</div>
                        <div><div class="button warehouse" @click="getListItems(data)"><i class="fas fa-list"></i></div></div>
                    </div>
                </div>
                <div>
                    <label class="full bordered">POS</label><br>
                    <label class="full bordered">Web</label><br>
                    <label class="full bordered">Assembly</label><br>
                    <label class="full">Total</label>
                </div>
                <div>
                    <span class="numbers full bordered">{{ data.store_quantity || '0' }}</span><br>
                    <span class="numbers full bordered">{{ data.web_quantity || '0' }}</span><br>
                    <span class="numbers full bordered">{{ data.assembly_quantity || '0' }}</span><br>
                    <span class="numbers full">{{ data.quantity || '0' }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_cost) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_cost) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_cost) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.cost) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_price) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.price) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.store_profit) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_profit) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_profit) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.profit) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ data.store_margin || '0.0' }}</span><br>
                    <span class="numbers full bordered">{{ data.web_margin || '0.0' }}</span><br>
                    <span class="numbers full bordered">{{ data.assembly_margin || '0.0' }}</span><br>
                    <span class="numbers full">{{ data.margin }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full bordered">&nbsp;</span><br>
                    <span class="numbers full">{{ percentOfTotal(data) }}</span>
                </div>
                <div>
                    <span class="numbers full bordered">{{ formatCurrency(data.pos_average_item_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.web_average_item_price) }}</span><br>
                    <span class="numbers full bordered">{{ formatCurrency(data.assembly_average_item_price) }}</span><br>
                    <span class="numbers full">{{ formatCurrency(data.average_item_price) }}</span>
                </div>
            </div>
        </div>

        <pagination v-if="showPagination" :paging="paging" @updatePage="goToPage"></pagination>

        <compare-dates :active="showCompare" :section="'warehouse'" @cancel="showCompare = false" />

    </div>
</template>
<script>
export default {
    name: 'warehouse',
    props: ['distributor_id'],
    data() {
        return {
            loading: true,
            message: '',
            filters: {
                section: 'warehouse',
                start_date: '',
                end_date: '',
                distributor_id: '',
                manufacturer_id: '',
                search_text: '',
                location_id: '',
                location_name: '',
                filter_by_list: '',
                sort_column: 'items_ordered',
                sort_descending: true
            },
            distributors: [],
            totals: {},
            paging: {total_pages: 0},
            items: [],
            distributor_name: '',
            manufacturerSearchList: [],
            manufactuerSearchMessage: '',
            itemSearchList: [],
            itemSearchMessage: '',
            dealerSearchList: [],
            dealerSearchMessage: '',
            minFilterDate: '',
            maxFilterDate: '',
            showCompare: false
        }
    },
    created() {
        // any filters previously stored for this section?
        if (this.getFilters.section == 'warehouse') {
            this.filters = this.getFilters // filters were stored last time we hit fetchDataItems()
        }
        this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
        this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
        this.minFilterDate = this.filters.start_date.split('-').shift() + '-01-01'
        this.maxFilterDate = this.filters.end_date.split('-').shift() + '-12-31'
        if (this.distributor_id) {
            this.filters.distributor_id = this.distributor_id
            this.fetchItems()
        } else {
            this.fetchData()
        }
    },
    computed: {
        showPagination() {
            return this.filters.distributor_id && this.items.length > 0 && this.paging.total_pages > 1
        },
        showDealerFilter() {
            return false; // this.getUser && this.getUser.user_type_code && this.getUser.user_type_code.indexOf('ADMIN') > -1
        }
    },
    methods: {
		fetchData() {
            this.totals = {}
            this.distributors = []
            this.loading = true
            this.message = 'Loading'
            this.storeFilters({}) // clear all stored filters
            this.apiCall({ destination: 'get_report_warehouse', data: this.filters }).then(obj => {
                this.loading = false
                if (obj.status == 'success') {
                    this.storeFilters(this.filters) // store current filter set
                    this.distributors = obj.distributors
                    this.items = []
                    this.totals = obj.totals
                    this.loading = false
                    this.message = ''
                    window.scrollTo(0,0);
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterDates() {
            this.$router.push({ path: this.$route.path , query: { start: this.filters.start_date, end: this.filters.end_date } })
        },
        goToPage(page) {
			this.paging.current_page = page
			this.setFilter('page')
		},
        showSort(column) {
            if (!this.filters.distributor_id) {
                return
            }
            if (this.filters.sort_column == column) {
                if (this.filters.sort_descending) {
                    return "<i class='fas fa-caret-down'></i>"
                } else {
                    return "<i class='fas fa-caret-up'></i>"
                }
            } else {
                return "&nbsp;"
            }
        },
        setSort(which) {
            //only sort if drilled down on a distributor and we're not already loading
            if (!this.distributor_id) { return }
            if (this.loading) { return }
            if (this.filters.sort_column == which) {
                this.filters.sort_descending = !this.filters.sort_descending
            } else {
                this.filters.sort_column = which
            }
            this.fetchItems()
        },
        setFilter(which, action) {
            switch (which) {
                case 'page':
                    this.filters.page = this.paging.current_page
                    this.fetchItems()
                    return false;
                case 'manufacturer':
                    this.manufacturerSearchList = []
                    this.manufactuerSearchMessage = ''
                    if (action == 'clear') {
                        this.filters.manufacturer_name = ''
                        this.filters.manufacturer_id = ''
                        this.filter_by_list = ''
                        if (this.filters.search_text) {
                            this.fetchItems()
                        } else {
                            this.fetchData()
                        }
                    } else {
                        this.searchFilter('manufacturer')
                    }
                    return false
                case 'search_text':
                    if (action == 'clear') {
                        this.filters.search_text = ''
                        if (this.filters.manufacturer_id) {
                            this.fetchItems()
                        } else {
                            this.fetchData()
                        }
                    } else {
                        this.fetchItems()
                    }
                    return false
                case 'location':
                    this.dealerSearchMessage = 'Dealer search coming soon'
                    setTimeout(() => { this.dealerSearchMessage = '' }, 2000)
                    //this.searchFilter('location')
                    return false
            }
        },
        searchFilter(which) {
            this.filters.filter_by_list = which
            this.apiCall({ destination: 'get_filter_list', data: this.filters }).then(obj => {
                if (obj.status == 'success') {
                    if (obj.filter_list.length == 1) {
                        this.filters.manufacturer_name = obj.filter_list[0]['description']
                        this.filters.manufacturer_id = obj.filter_list[0]['manufacturer_id']
                        this.fetchItems()
                    } else if (obj.filter_list.length > 1) {
                        this.manufacturerSearchList = obj.filter_list
                    } else {
                        this.manufactuerSearchMessage = 'Manufacturer not found'
                    }
                } else {
                    this.message = obj.message
                }
            })
        },
        setFilterFromSearch(data) {
            if (this.filters.filter_by_list == 'manufacturer') {
                this.manufacturerSearchList = []
                this.manufactuerSearchMessage = ''
                this.filters.manufacturer_name = data.description
                this.filters.manufacturer_id = data['manufacturer_id']
            }
            if (this.filters.filter_by_list == 'location') {
                this.dealerSearchList = []
                this.dealerSearchMessage =  ''
                this.filters.location_name = data.description
                this.filters.location_id = data['location_id']
            }
            this.message = ''
            this.fetchItems()
        },
        clearFilters() {
            this.filters = {
                section: 'distributors',
                start_date: '',
                end_date: '',
                distributor_id: '',
                search_text: '',
                location_id: '',
                location_name: '',
                filter_by_list: '',
            }
            this.filters.start_date = this.getQueryString('start') || this.getYearStartDate()
            this.filters.end_date = this.getQueryString('end') || this.getYesterdaysDate()
            this.items = []
            this.distributor_name = ''
            this.message = ''
            if (this.$route.path != '/warehouse') {
                // pushing route will clear id
                this.$router.push({path: '/warehouse', query: this.$route.query})
            } else {
                // but if base route already, reload data
                this.fetchData()
            }
        },
        getListItems(data) {
            if (data.distributor_id) {
                this.$router.push({path: '/warehouse/' + data.distributor_id, query: this.$route.query})
            } else if (data.product_id) {
                this.$router.push({path: '/item/warehouse/' + data.product_id, query: this.$route.query})
            }
        },
        fetchItems() {
            this.totals = {}
            this.distributors = []
            this.loading = true
            this.message = 'Loading'
            this.apiCall({ destination: 'get_report_warehouse_items', data: this.filters }).then(obj => {
                this.loading = false
                if (obj.status == 'success') {
                    this.storeFilters(this.filters) // store current filter set
                    this.distributor_name = obj.distributor_name
                    this.items = obj.items
                    this.paging = obj.paging
                    this.totals = this.getTotals
                    this.message = ''
                } else {
                    this.message = obj.message
                }
            })
        },
        totalQuantity(data) {
            return Number(data.pos.quantity) + Number(data.web.quantity) + Number(data.assembly.quantity)
        },
        totalCost(data) {
            return Number(data.pos.cost) + Number(data.web.cost) + Number(data.assembly.cost)
        },
        totalPrice(data) {
            return Number(data.pos.price) + Number(data.web.price) + Number(data.assembly.price)
        },
        totalProfit(data) {
            return this.formatCurrency(Number(data.pos.profit) + Number(data.web.profit) + Number(data.assembly.profit))
        },
        totalMargin(data) {
            return this.totalPrice(data) > 0 ? (100 * (1 - Number(this.totalCost(data)) / Number(this.totalPrice(data)))).toFixed(1) : '0.00'
        },
        percentOfTotal(data) {
            return ((Number(data.price) / this.totals.price) * 100).toFixed(1)
        },
        averageItemPricePOS(data) {
            return this.formatCurrency(Number(data.pos.price) / Number(data.pos.quantity))
        },
        averageItemPriceWeb(data) {
            return this.formatCurrency(Number(data.web.price) / Number(data.web.quantity))
        },
        averageItemPriceAssembly(data) {
            return this.formatCurrency(Number(data.assembly.price) / Number(data.assembly.quantity))
        },
        averageItemPrice(data) {
            return this.formatCurrency(Number(this.totalPrice(data)) / Number(this.totalQuantity(data)))
        }
    }
}
</script>
<style lang="scss" scoped>
.report-row {
    grid-template-columns: 2.5fr 0.5fr 1fr 1fr 1fr 0.75fr 0.75fr 0.75fr 0.75fr;
}
.row-controls {
    grid-template-columns: 3fr 1fr;
    label { width: auto; padding: 0 0.5em 0 0.5em; }
}
</style>
